<script>
import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation.js";
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import axios from "axios";
import {useBankStore} from "@/stores/Bank";

export default {

    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();
        const bank = ref({
            agencies: [],
            fieldsMapper: []
        });
        const bankStore = useBankStore();

        function findById(id) {
            loading.value = true;
            axios.get(`/banks/${id}`).then((response) => {
                bank.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/banks', bank.value).then((response) => {
                    bankStore.addBank(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/banks', bank.value).then((response) => {
                    bankStore.updateBank(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/banks/${bank.value.id}`).then(() => {
                    bankStore.deleteBank(bank.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            readonly,
            bankStore,
            mxRules,
            dialog,
            loading,
            overlay,
            bank,
            save,
            update,
            remove,
            getBack,
        };
    },
};
</script>

<template>
    <div>
        <v-dialog v-model="dialog" max-width="800" persistent>
            <v-card>
                <w-dialog-toolbar
                    :view-action="viewAction"
                    :delete-action="deleteAction"
                    :add-action="addAction"
                    :update-action="updateAction"
                ></w-dialog-toolbar>
                <w-overlay
                    :overlay="overlay"
                    :loading="loading"
                    :delete-action="deleteAction"
                    @remove="remove"
                    @cancel="overlay = false"
                ></w-overlay>
                <v-card-text>
                    <v-container fluid>
                        <v-form ref="form" lazy-validation>
                            <v-row dense>
                                <v-col cols="6">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-text-field
                                        v-model="bank.code"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.name')" required/>
                                    <w-text-field
                                        v-model="bank.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                            <br/>
                            <w-fieldset
                                :title="$t('bank.agencies')"
                                :show-add-action="!readonly"
                                @add="bank.agencies.push({})"
                            >
                                <v-row
                                    v-for="(agency, index) in bank.agencies"
                                    :key="index"
                                    dense
                                >
                                    <v-col cols="5">
                                        <w-label :label="$t('common.code')" required/>
                                        <w-text-field
                                            v-model="agency.code"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.name')" required/>
                                        <w-text-field
                                            v-model="agency.name"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="1">
                                        <w-btn-icon
                                            :disabled="readonly"
                                            icon="mdi-trash-can"
                                            color="red"
                                            classe="mt-6"
                                            @press="bank.agencies.splice(index, 1)"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn-add
                        :show="addAction"
                        :loading="loading"
                        @press="save(false)"
                    />
                    <w-btn-update
                        :show="updateAction"
                        :loading="loading"
                        @press="update"
                    />
                    <w-btn-delete
                        :show="deleteAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>